/**
 * PRISM workbench — the pre-send review trial (A7Y-5454).
 *
 * Every emailed audit is read by the Expert Eye before its email goes. This
 * panel shows the trial counters, the emails held right now with the findings
 * that hold them, and a Release button that sends a held email now. Counts and
 * hold state come from the API (`buildPrismPreSendWorkbench`); nothing is
 * re-derived here.
 */
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  PlatformHealthPrismPreSendResponse,
  PrismPreSendAdjudicationItem,
} from '@/pages/platform-health/types';
import type { PrismPreSendFinding, PrismPreSendReviewDoc } from '@a7y/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, MailCheck } from 'lucide-react';
import { useState } from 'react';

const PRE_SEND_QUERY_KEY = ['platform-health-prism-pre-send'];
const REFRESH_INTERVAL_MS = 60 * 1000;

function when(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : '—';
}

function seconds(ms: number | null) {
  return ms === null ? '—' : `${(ms / 1000).toFixed(1)} s`;
}

const ACTION_LABEL: Record<PrismPreSendFinding['action'], string> = {
  hold: 'Holds',
  file: 'Files',
  queue_adjudication: 'Needs a ruling',
  none: 'Recorded',
};

const ACTION_TONE: Record<PrismPreSendFinding['action'], string> = {
  hold: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  file: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  queue_adjudication: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  none: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-200',
};

function Stat({
  label,
  value,
  alert = false,
}: {
  label: string;
  value: string | number;
  alert?: boolean;
}) {
  return (
    <div className="rounded-md border border-gray-200 px-3 py-2 dark:border-zinc-700">
      <dt className="text-[11px] text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-lg font-semibold tabular-nums text-gray-950 dark:text-white',
          alert && Number(value) > 0 && 'text-red-700 dark:text-red-300'
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function FindingLine({ finding }: { finding: PrismPreSendFinding }) {
  return (
    <li className="flex gap-2 py-1 text-xs text-gray-700 dark:text-gray-200">
      <span
        className={cn(
          'h-fit shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          ACTION_TONE[finding.action]
        )}
      >
        {ACTION_LABEL[finding.action]}
      </span>
      <span>
        {finding.claim}
        <span className="ml-1 text-gray-400 dark:text-gray-500">
          {finding.ruleId ? `rule ${finding.ruleId}` : finding.class.replace(/_/g, ' ')} ·{' '}
          {finding.engine} · {finding.severity}
        </span>
      </span>
    </li>
  );
}

function HeldEmail({ review }: { review: PrismPreSendReviewDoc }) {
  const queryClient = useQueryClient();
  const release = useMutation({
    mutationFn: () => api.releasePrismPreSendHold(review.auditId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PRE_SEND_QUERY_KEY }),
  });
  const holding = review.findings.filter((f) => f.action === 'hold');
  return (
    <li
      data-testid="prism-pre-send-held"
      className="border-t border-gray-100 px-4 py-3 first:border-t-0 dark:border-zinc-700"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {review.appTitle ?? `Untitled app ${review.appId ?? '(no app id recorded)'}`}
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {review.store} · {review.emailKind === 'run' ? 'scheduled run email' : 'audit email'}{' '}
              · audit {review.auditId}
            </span>
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Held {when(review.heldAt)} ·{' '}
            {review.releaseAfter
              ? `goes out on its own ${when(review.releaseAfter)}`
              : 'held until released here or replaced by a later audit'}
          </p>
        </div>
        <button
          type="button"
          disabled={release.isPending || release.isSuccess}
          onClick={() => release.mutate()}
          className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
        >
          <MailCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {release.isSuccess ? 'Released' : release.isPending ? 'Releasing…' : 'Release email now'}
        </button>
      </div>
      {release.isError ? (
        <p className="mt-1 text-xs text-red-700 dark:text-red-300">
          {release.error instanceof Error ? release.error.message : 'Release failed.'}
        </p>
      ) : null}
      <ul className="mt-2">
        {(holding.length > 0 ? holding : review.findings).map((finding, i) => (
          <FindingLine key={`${review.auditId}:${i}`} finding={finding} />
        ))}
      </ul>
    </li>
  );
}

function AdjudicationItem({ item }: { item: PrismPreSendAdjudicationItem }) {
  const selector = item.decisionSelector;
  return (
    <li
      data-testid="prism-pre-send-adjudication"
      className="border-t border-gray-100 px-4 py-2 text-xs first:border-t-0 dark:border-zinc-700"
    >
      <p className="text-gray-800 dark:text-gray-100">{item.claim}</p>
      <p className="mt-0.5 text-gray-500 dark:text-gray-400">
        “{item.evidenceQuote}” ·{' '}
        {item.appTitle ?? `Untitled app ${item.appId ?? '(no app id recorded)'}`} · {item.store} ·
        audit {item.auditId} · {when(item.createdAt)}
      </p>
      <p className="mt-0.5 font-mono text-[11px] text-gray-500 dark:text-gray-400">
        {selector.keySpace} · {selector.engine} · {selector.defectKind} · {selector.pathPrefix}
      </p>
    </li>
  );
}

/** How many more flagged reviews each "Show more" reads. */
const ADJUDICATION_PAGE = 500;

function adjudicationEmptyText(payload: PlatformHealthPrismPreSendResponse): string {
  const { listed, total } = payload.adjudicationReviews;
  if (total === 0) return 'No unknown finding is waiting for a ruling.';
  if (total === null)
    return 'No unruled finding in the reviews read; the count of flagged reviews could not be read.';
  return listed < total
    ? `No unruled finding in the ${listed} flagged reviews read of ${total}.`
    : `Every finding in the ${total} flagged reviews has been ruled.`;
}

export function PrismPreSendContent({
  payload,
  onShowMore,
}: {
  payload: PlatformHealthPrismPreSendResponse;
  onShowMore?: () => void;
}) {
  const { counters, trial } = payload;
  const { listed, total } = payload.adjudicationReviews;
  return (
    <div className="space-y-3 px-4 py-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {trial.startedAt
          ? `Trial started ${when(trial.startedAt)} · ends ${when(trial.endsAt)} (${trial.trialDays} days)`
          : 'The trial starts with the first audit email reviewed in this environment.'}
      </p>
      <dl className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
        <Stat label="Audits judged" value={counters.judged} />
        <Stat label="Held" value={counters.held} />
        <Stat label="Held now" value={counters.heldNow} alert />
        <Stat label="Released" value={counters.released} />
        <Stat label="Judge errors (sent unreviewed)" value={counters.errors} alert />
        <Stat label="Skipped" value={counters.skipped} />
        <Stat
          label="Added delay, mean / p95"
          value={`${seconds(counters.meanReviewLatencyMs)} / ${seconds(counters.p95ReviewLatencyMs)}`}
        />
        <Stat label="Judge cost" value={`$${counters.costInCurrency.toFixed(2)}`} />
      </dl>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Findings: {counters.findingsByAction.hold} held an email · {counters.findingsByAction.file}{' '}
        to file · {counters.findingsByAction.queue_adjudication} need a ruling ·{' '}
        {counters.findingsByAction.none} recorded.
      </p>
      <div className="rounded-md border border-gray-200 dark:border-zinc-700">
        <h4 className="border-b border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 dark:border-zinc-700 dark:text-gray-200">
          Emails held now
        </h4>
        {payload.heldNow.length === 0 ? (
          <p className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
            No audit email is held.
          </p>
        ) : (
          <ul>
            {payload.heldNow.map((review) => (
              <HeldEmail key={review.auditId} review={review} />
            ))}
          </ul>
        )}
      </div>
      <div className="rounded-md border border-gray-200 dark:border-zinc-700">
        <h4 className="border-b border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 dark:border-zinc-700 dark:text-gray-200">
          Needs a ruling — {payload.adjudication.length} unknown finding
          {payload.adjudication.length === 1 ? '' : 's'} from {listed}
          {total === null ? '' : ` of ${total}`} flagged reviews
        </h4>
        {payload.adjudication.length === 0 ? (
          <p className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
            {adjudicationEmptyText(payload)}
          </p>
        ) : (
          <ul>
            {payload.adjudication.map((item, i) => (
              <AdjudicationItem key={`${item.auditId}:${i}`} item={item} />
            ))}
          </ul>
        )}
        {total !== null && listed < total && onShowMore ? (
          <button
            type="button"
            onClick={onShowMore}
            className="mx-4 mb-2 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            Show more flagged reviews
          </button>
        ) : null}
        <p className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500 dark:border-zinc-700 dark:text-gray-400">
          A ruling is an adjudication in the PRISM decision store on the selector shown: confirmed
          defect makes the finding known (it then files, or holds at high severity); false positive
          stops it.
        </p>
      </div>
    </div>
  );
}

export function PrismPreSendPanel() {
  const [adjudicationLimit, setAdjudicationLimit] = useState(ADJUDICATION_PAGE);
  const query = useQuery({
    queryKey: [...PRE_SEND_QUERY_KEY, adjudicationLimit],
    queryFn: () => api.getPlatformHealthPrismPreSend({ adjudicationLimit }),
    refetchInterval: REFRESH_INTERVAL_MS,
    retry: false,
  });
  return (
    <section
      data-testid="prism-pre-send"
      className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800"
    >
      <header className="border-b border-gray-200 px-4 py-2.5 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Pre-send review trial
        </h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          The Expert Eye reads every audit before its email goes. A known high-severity finding
          holds the email for up to 24 hours, and an audit with no action plan holds it until it
          is released here or replaced by a later audit; the audit itself is published either way.
        </p>
      </header>
      {query.isError ? (
        <p
          data-testid="prism-pre-send-error"
          className="px-4 py-3 text-xs text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
          Pre-send reviews could not be read.
        </p>
      ) : query.data ? (
        <PrismPreSendContent
          payload={query.data.data}
          onShowMore={() => setAdjudicationLimit((limit) => limit + ADJUDICATION_PAGE)}
        />
      ) : (
        <p className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
          Loading pre-send reviews…
        </p>
      )}
    </section>
  );
}
